import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../../components/AppLayout";
import { api } from "../../api/client";
import { Plus, X, Trash2, Search, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function AdminEnrollments() {
    const [list, setList] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [user, setUser] = useState("");
    const [course, setCourse] = useState("");
    const [search, setSearch] = useState("");

    const load = async () => {
        try {
            const [e, cu, co] = await Promise.all([
                api.get("/admin/enrollments"),
                api.get("/admin/customers"),
                api.get("/admin/courses"),
            ]);
            setList(e); setCustomers(cu); setCourses(co);
        } catch { toast.error("Load failed"); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => list.filter((e) => !search || (e.user_name + e.user_email + e.course_title).toLowerCase().includes(search.toLowerCase())), [list, search]);

    const enrol = async () => {
        if (!user || !course) return toast.error("Pick a student and a course");
        try {
            await api.post("/admin/enrollments", { user_id: user, course_id: course });
            toast.success("Student enrolled");
            setOpen(false); setUser(""); setCourse(""); load();
        } catch (err) { toast.error(err.status === 409 ? "Already enrolled" : "Failed"); }
    };

    const revoke = async (e) => {
        if (!window.confirm(`Revoke ${e.user_name}'s access to ${e.course_title}?`)) return;
        try {
            await fetch(`${api.API_BASE}/admin/enrollments?user_id=${encodeURIComponent(e.user_id)}&course_id=${encodeURIComponent(e.course_id)}`, { method: "DELETE", credentials: "include" })
                .then((r) => { if (!r.ok) throw new Error(); });
            toast.success("Revoked");
            load();
        } catch { toast.error("Failed"); }
    };

    return (
        <AppLayout searchPlaceholder="Search enrollments...">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Enrollments</h1>
                    <p className="text-sm text-gray-400 mt-2">{list.length} active enrollments • Manually enrol or revoke access</p>
                </div>
                <button data-testid="manual-enrol-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5">
                    <Plus className="w-4 h-4" /> Manual Enrol
                </button>
            </div>

            <div className="mb-6 relative max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input data-testid="search-enrollments" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by student or course..." className="w-full bg-ink-700 border border-white/5 rounded-full pl-11 pr-5 py-3 text-sm focus:outline-none focus:border-lime/40" />
            </div>

            {loading ? <div className="text-gray-500">Loading…</div> : list.length === 0 ? (
                <div className="bg-ink-700 border border-white/5 rounded-3xl p-12 text-center">
                    <GraduationCap className="w-10 h-10 text-gray-600 mx-auto mb-4" />
                    <h3 className="font-display font-bold text-xl">No enrollments yet</h3>
                    <p className="text-sm text-gray-400 mt-2">Once students purchase or you manually enrol them, they'll appear here.</p>
                    <button onClick={() => setOpen(true)} className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold"><Plus className="w-4 h-4" /> Manual Enrol</button>
                </div>
            ) : (
                <div className="bg-ink-700 rounded-3xl border border-white/5 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-xs uppercase text-gray-500 tracking-widest bg-ink-800">
                                    <th className="text-left font-medium px-6 py-4">Student</th>
                                    <th className="text-left font-medium px-6 py-4">Course</th>
                                    <th className="text-left font-medium px-6 py-4">Progress</th>
                                    <th className="text-left font-medium px-6 py-4">Lessons</th>
                                    <th className="text-left font-medium px-6 py-4">Enrolled</th>
                                    <th className="text-right font-medium px-6 py-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((e) => (
                                    <tr key={e.id} data-testid={`enrol-row-${e.id}`} className="border-t border-white/5 hover:bg-ink-800/50">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                {e.user_avatar ? <img src={e.user_avatar} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-grape/20 flex items-center justify-center text-xs font-bold">{e.user_name?.[0] || "?"}</div>}
                                                <div>
                                                    <div className="text-sm font-semibold">{e.user_name}</div>
                                                    <div className="text-xs text-gray-500">{e.user_email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm">{e.course_title}</td>
                                        <td className="px-6 py-4 w-44">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-1 h-1.5 rounded-full bg-ink-600 overflow-hidden">
                                                    <div className="h-full bg-gradient-to-r from-grape to-lime" style={{ width: `${e.progress}%` }} />
                                                </div>
                                                <span className="text-xs font-semibold text-lime">{e.progress}%</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm">{e.completed_lessons}</td>
                                        <td className="px-6 py-4 text-xs text-gray-400">{e.enrolled_at ? new Date(e.enrolled_at).toLocaleDateString("en-GB") : "—"}</td>
                                        <td className="px-6 py-4 text-right">
                                            <button data-testid={`revoke-${e.id}`} onClick={() => revoke(e)} className="w-8 h-8 rounded-full hover:bg-red-500/20 hover:text-red-400 text-gray-500 inline-flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6" onClick={() => setOpen(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-ink-700 rounded-3xl border border-white/10 p-7">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-display font-bold text-xl">Manual Enrolment</h2>
                            <button onClick={() => setOpen(false)} className="text-gray-400"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 mb-2">Student (real users only)</label>
                                <Select value={user} onValueChange={setUser}>
                                    <SelectTrigger data-testid="enrol-student" className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 h-12 text-white"><SelectValue placeholder="Select student..." /></SelectTrigger>
                                    <SelectContent className="bg-ink-800 border border-white/10 text-white">
                                        {customers.filter((c) => !c.is_demo).map((c) => <SelectItem key={c.id} value={c.id} className="text-white focus:bg-ink-700 focus:text-white">{c.name} — {c.email}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-2">Course</label>
                                <Select value={course} onValueChange={setCourse}>
                                    <SelectTrigger data-testid="enrol-course" className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 h-12 text-white"><SelectValue placeholder="Select course..." /></SelectTrigger>
                                    <SelectContent className="bg-ink-800 border border-white/10 text-white">
                                        {courses.map((c) => <SelectItem key={c.id} value={c.id} className="text-white focus:bg-ink-700 focus:text-white">{c.title}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="mt-7 flex justify-end gap-3">
                            <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-full border border-white/10 text-sm">Cancel</button>
                            <button onClick={enrol} data-testid="confirm-enrol" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold"><Plus className="w-4 h-4" /> Enrol</button>
                        </div>
                    </div>
                </div>
            )}
        </AppLayout>
    );
}
